import * as React from 'react'
import {faSpinner} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'

import {type NonEmptyArray} from '@freckle/non-empty'
import {Alert} from '@frontrow/console/src/components/ui/alert'
import {Button} from '@frontrow/console/src/components/button'
import {H2} from '@frontrow/console/src/components/typography'
import {type StarStatus} from '@frontrow/console/src/entities/models/school'

import {
  type DetailedSchoolT,
  type WeakRenaissanceSchoolClientT
} from '@frontrow/console/src/models/school'
import {RenaissanceConfigurationFormFields} from '@frontrow/console/src/components/common/renaissance-configuration-form-fields'
import Wrapper from '@frontrow/console/src/components/pages/bulk-edit-school-configuration/wrapper'
import {renaissanceFieldsRequired} from '@frontrow/console/src/helpers/validations/school'
import {EditableRpIdentifierTable} from '@frontrow/console/src/components/pages/bulk-edit-school-configuration/editable-rp-identifier-table'
import {spacedWrapper, alertWrapper} from './bulk-edit-school-configuration.module.scss'
import LabelledInput from '@frontrow/console/src/components/common/labelled-input'
import {RenaissanceIDPError} from '@frontrow/console/src/components/common/renaissance-idp-error'

type Props = {
  canEditForm: boolean
  districtIdsSet: Set<number>
  rpIdentifiersToUpdate: {
    [schoolId: string]: string
  }
  schoolData: NonEmptyArray<DetailedSchoolT>
  shouldSetRosteringSourceToRenaissance: boolean
  allSchoolsHaveRenaissanceAsIdp: boolean
  baseUrl: string
  clientId: string
  rpId: string
  starStatus: StarStatus
  batchUpdateInProgress: boolean
  renaissancePlatform: string
  schoolsWithoutRpIdentifier: Array<DetailedSchoolT>
  schoolsWithRpIdentifier: Array<DetailedSchoolT>
  needsStarIntegrationFields: boolean
  showRenaissanceIDPError: boolean
  submitDisabled: boolean
  weakRenaissanceSchoolClient: WeakRenaissanceSchoolClientT
  onUpdateRpIdentifier: (schoolId: number, rpIdentifier?: string | null) => void
  onUpdateClientId: (clientId?: string | null) => void
  onUpdateRpId: (rpId?: string | null) => void
  onUpdateBaseUrl: (baseUrl?: string | null) => void
  onUpdateRenaissancePlatform: (e: React.ChangeEvent<HTMLInputElement>) => void
  onUpdateStarStatus: (starStatus: StarStatus) => void
  onUpdateSchools: () => void
  onToggleShouldSetRosteringSourceToRenaissance: () => void
}

export function BulkEditSchoolConfiguration(props: Props): React.ReactElement {
  const {
    canEditForm,
    districtIdsSet,
    schoolData,
    shouldSetRosteringSourceToRenaissance,
    allSchoolsHaveRenaissanceAsIdp,
    weakRenaissanceSchoolClient,
    starStatus,
    renaissancePlatform,
    needsStarIntegrationFields,
    showRenaissanceIDPError,
    clientId,
    rpId,
    baseUrl,
    schoolsWithoutRpIdentifier,
    schoolsWithRpIdentifier,
    batchUpdateInProgress,
    submitDisabled,
    rpIdentifiersToUpdate,
    onUpdateClientId,
    onUpdateBaseUrl,
    onUpdateRpId,
    onUpdateRenaissancePlatform,
    onUpdateStarStatus,
    onUpdateSchools,
    onUpdateRpIdentifier,
    onToggleShouldSetRosteringSourceToRenaissance
  } = props

  const showStarSyncFreshlyEnabledNotification =
    schoolData[0].renaissanceSchoolClient !== null &&
    schoolData[0].renaissanceSchoolClient !== undefined &&
    schoolData[0].renaissanceSchoolClient.starStatus !== 'star_enabled' &&
    starStatus === 'star_enabled'

  return (
    <Wrapper>
      {canEditForm && (
        <>
          <MultipleDistrictsSelectedNotification
            numDistinctDistrictIds={Array.from(districtIdsSet).length}
          />
          <div>
            You have selected {schoolData.length} schools.{' '}
            <strong>
              Updating will override any previously saved configurations for these schools.
            </strong>
          </div>
        </>
      )}

      {!canEditForm && (
        <Alert type="warning" dataTest="varying-configuration-alert">
          You have selected schools that currently have different Renaissance configuration
          settings. In order to update configuration, please make sure all selected schools have the
          same Renaissance Configuration (Client ID, RPID, Base URL, Renaissance platform, and Star
          Assessment integration).
        </Alert>
      )}

      <div className="mt-4">
        <RenaissanceConfigurationFormFields
          renaissanceFieldsRequired={renaissanceFieldsRequired(
            shouldSetRosteringSourceToRenaissance,
            weakRenaissanceSchoolClient
          )}
          renaissanceSchoolClientClientId={getTextFieldValue(clientId, canEditForm)}
          renaissanceSchoolClientRpid={getTextFieldValue(rpId, canEditForm)}
          renaissanceSchoolClientBaseUrl={getTextFieldValue(baseUrl, canEditForm)}
          renaissanceSchoolClientStarStatus={starStatus}
          showStarSyncFreshlyEnabledNotification={showStarSyncFreshlyEnabledNotification}
          renaissanceSchoolClientPlatform={renaissancePlatform}
          needsStarIntegrationFields={needsStarIntegrationFields}
          showRenaissanceIDPError={showRenaissanceIDPError}
          onChangeClientId={onUpdateClientId}
          onChangeBaseUrl={onUpdateBaseUrl}
          onChangeRpid={onUpdateRpId}
          onChangeRenaissancePlatform={onUpdateRenaissancePlatform}
          onUpdateStarStatus={onUpdateStarStatus}
          disabled={!canEditForm}
        />
      </div>

      <div className={spacedWrapper}>
        <H2>Identity Provider (IdP)</H2>
        {showRenaissanceIDPError && (
          <div className={alertWrapper}>
            <Alert type="danger">
              <RenaissanceIDPError />
            </Alert>
          </div>
        )}
        {!allSchoolsHaveRenaissanceAsIdp && (
          <LabelledInput label="Set rostering source for all schools to Renaissance">
            <input
              type="checkbox"
              checked={shouldSetRosteringSourceToRenaissance}
              onChange={onToggleShouldSetRosteringSourceToRenaissance}
              disabled={!canEditForm}
              data-test="set-rostering-source-to-renaissance-checkbox"
            />
          </LabelledInput>
        )}
        {allSchoolsHaveRenaissanceAsIdp && (
          <div data-test="cannot-disable-renaissance-rostering-message">
            All of these schools have Renaissance as their rostering source. To change rostering
            method, please edit each school individually.
          </div>
        )}
      </div>

      {schoolsWithoutRpIdentifier.length > 0 && canEditForm && (
        <div className={alertWrapper}>
          <Alert type="warning">
            {schoolsWithoutRpIdentifier.length} of the selected schools do not have RP Identifiers
            (School GUIDs). Add them now in the list below or at a later time to complete the
            configuration.
          </Alert>
        </div>
      )}

      <Button
        addClass={spacedWrapper}
        onClick={onUpdateSchools}
        disabled={submitDisabled || !canEditForm}
        dataTest="submit-button"
      >
        <SubmitButtonBody isSubmissionInProgress={batchUpdateInProgress} />
      </Button>

      {schoolsWithoutRpIdentifier.length > 0 && canEditForm && (
        <div className={spacedWrapper}>
          The following schools do not have RP Identifiers (School GUIDs):
          <EditableRpIdentifierTable
            schools={schoolsWithoutRpIdentifier}
            schoolsRpIdentifierMap={rpIdentifiersToUpdate}
            onUpdateRpIdentifier={onUpdateRpIdentifier}
          />
        </div>
      )}

      {schoolsWithRpIdentifier.length > 0 && canEditForm && (
        <div className={spacedWrapper}>
          RP Identifiers (School GUIDs) are required to complete the configuration. Review the
          following stored RP Identifiers and update if necessary.
          <EditableRpIdentifierTable
            schools={schoolsWithRpIdentifier}
            schoolsRpIdentifierMap={rpIdentifiersToUpdate}
            onUpdateRpIdentifier={onUpdateRpIdentifier}
          />
        </div>
      )}
    </Wrapper>
  )
}

function MultipleDistrictsSelectedNotification({
  numDistinctDistrictIds
}: {
  numDistinctDistrictIds: number
}): React.ReactElement {
  return numDistinctDistrictIds > 1 ? (
    <div className={alertWrapper}>
      <Alert type="warning">
        You have selected schools with{' '}
        <strong>{numDistinctDistrictIds} different District IDs</strong>.
      </Alert>
    </div>
  ) : (
    <></>
  )
}

function SubmitButtonBody({
  isSubmissionInProgress
}: {
  isSubmissionInProgress: boolean
}): React.ReactElement {
  return isSubmissionInProgress ? (
    <>
      <FontAwesomeIcon icon={faSpinner} spin /> Updating Schools...
    </>
  ) : (
    <>Update Schools</>
  )
}

function getTextFieldValue(value: string, canEditForm: boolean): string {
  return canEditForm ? value : 'Varies across selected schools'
}
